import type { ReactNode } from 'react'
import { ResolvingRegion } from './ResolvingRegion'

/**
 * The page's own fixed words — the ones the page states rather than reads. Here rather than
 * in the page, because its skeleton has to show the same ones: static text greyed out would
 * hide what the page already knows and then resize when the read lands. (A card that names
 * itself owns its own heading, and exports it for the same reason.)
 */
export const PRODUCT_PAGE_EYEBROW = 'Product · Element inventory'

/**
 * The brand kit's heading. The page's rather than the card's: the card is a generic
 * asset list and is told what it is standing for, so the words belong to whoever uses it.
 */
export const BRAND_KIT_TITLE = 'Brand kit'

export interface ProductPageLayoutProps {
	/** `Region / Market / Brand / Product` as a path. */
	breadcrumb: ReactNode
	/** Eyebrow and the name the page resolved for its geography. */
	header: ReactNode
	/** The hierarchy the page resolves against. */
	hierarchy: ReactNode
	/** The filter rail — the side column, at a fixed width from `lg` up. */
	rail: ReactNode
	/** The wide column: naming, brand kit, texts, campaigns, delivered assets, applicable rules, assigned PM. */
	main: ReactNode
	/** True while every answer in the content column still belongs to the previous scope. */
	resolving?: boolean
}

/**
 * The Product page's shape, with nothing in it: the identity band (breadcrumb, header,
 * hierarchy bar) over a rail and one column of cards.
 *
 * One component rather than markup in the page, because the page's first-read skeleton has to
 * stand in this exact shape — that is its whole purpose, and a copy of the grid in each would
 * have nothing keeping the two in step. The skeleton fills the slots with placeholders; the
 * page fills them with cards.
 *
 * Owns the two columns and the gap between them. Each slot keeps its own outer margin, as the
 * bar and the header do for every page they appear on.
 */
export function ProductPageLayout({ breadcrumb, header, hierarchy, rail, main, resolving }: ProductPageLayoutProps) {
	return (
		<div>
			{breadcrumb}
			{header}
			{hierarchy}

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
				{rail}

				<ResolvingRegion resolving={resolving} className="space-y-4">
					{main}
				</ResolvingRegion>
			</div>
		</div>
	)
}
